
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabaseClient';
import NotificationsPanel from './NotificationsPanel';

interface DashboardHeaderProps {
  userEmail: string;
  onLogout: () => void;
}

const DashboardHeader = ({ userEmail, onLogout }: DashboardHeaderProps) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notificacoesNaoLidas, setNotificacoesNaoLidas] = useState(0);
  const [nomeUsuario, setNomeUsuario] = useState('');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    carregarPerfil();
  }, [userEmail]);

  const carregarPerfil = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('nome, avatar_url')
        .eq('id', user.id)
        .maybeSingle();

      if (profile) {
        setNomeUsuario(profile.nome || '');
        setAvatarUrl(profile.avatar_url || null);
      }
    } catch (err) {
      console.error('Erro ao carregar perfil:', err);
    }
  };

  useEffect(() => {
    const atualizarContagem = () => {
      const stored = localStorage.getItem('notificacoes');
      if (stored) {
        const notificacoes = JSON.parse(stored);
        const naoLidas = notificacoes.filter((n: any) => !n.lida).length;
        setNotificacoesNaoLidas(naoLidas);
      }
    };

    atualizarContagem();
    const interval = setInterval(atualizarContagem, 5000);
    return () => clearInterval(interval);
  }, []);

  const getInitials = (nome: string, email: string) => {
    if (nome && nome.trim()) {
      return nome
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return email.slice(0, 2).toUpperCase();
  };

  const nomeExibicao = nomeUsuario
    ? nomeUsuario.split(' ')[0]
    : userEmail.split('@')[0];

  return (
    <>
      <header className="bg-luxury-dark/60 backdrop-blur-xl border-b border-gold-500/20 sticky top-0 z-50">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-28">
            {/* Logo */}
            <a href="/" className="flex items-center gap-3 cursor-pointer group">
              <img
                src="https://static.readdy.ai/image/9b313db6312c9605d9f9e3f145d3e29c/a68d70c6b66064870b0e9282c113321b.jpeg"
                alt="Carta do Eu Futuro"
                className="w-20 h-20 object-contain group-hover:scale-110 group-hover:drop-shadow-[0_0_14px_rgba(201,164,64,0.55)] transition-all duration-300"
              />
              <span className="font-['Playfair_Display'] text-2xl font-semibold text-white hidden sm:block">
                Carta do Eu Futuro
              </span>
            </a>

            {/* Ações do usuário */}
            <div className="flex items-center gap-4">
              {/* Botão de Notificações */}
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative w-10 h-10 flex items-center justify-center rounded-full bg-gold-500/10 border border-gold-500/30 hover:bg-gold-500/20 transition-all duration-300 cursor-pointer"
              >
                <i className="ri-notification-3-line text-2xl text-gold-400"></i>
                {notificacoesNaoLidas > 0 && (
                  <span className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white text-sm font-bold rounded-full flex items-center justify-center animate-pulse">
                    {notificacoesNaoLidas > 9 ? '9+' : notificacoesNaoLidas}
                  </span>
                )}
              </button>

              {/* Menu do usuário */}
              <div className="relative">
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="flex items-center gap-3 bg-gold-500/10 border border-gold-500/30 rounded-lg px-4 py-2 hover:bg-gold-500/20 transition-all duration-300 cursor-pointer"
                >
                  {/* Avatar */}
                  <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 border-2 border-gold-500/40">
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt={nomeExibicao}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-gold-500 to-gold-600 flex items-center justify-center">
                        <span className="text-luxury-black text-sm font-bold">
                          {getInitials(nomeUsuario, userEmail)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Nome */}
                  <div className="hidden sm:flex flex-col items-start">
                    <span className="font-['Montserrat'] text-sm font-semibold text-white leading-tight">
                      {nomeExibicao}
                    </span>
                    <span className="font-['Montserrat'] text-xs text-white/40 leading-tight truncate max-w-[140px]">
                      {userEmail}
                    </span>
                  </div>

                  <i className={`ri-arrow-down-s-line text-gold-400 text-xl transition-transform duration-300 ${showMenu ? 'rotate-180' : ''}`}></i>
                </button>

                {/* Dropdown menu */}
                {showMenu && (
                  <div className="absolute right-0 mt-2 w-64 bg-luxury-dark border border-gold-500/30 rounded-lg shadow-2xl overflow-hidden z-50">
                    {/* Info do usuário no topo */}
                    <div className="p-4 border-b border-gold-500/20 flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 border-2 border-gold-500/40">
                        {avatarUrl ? (
                          <img src={avatarUrl} alt={nomeExibicao} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-gold-500 to-gold-600 flex items-center justify-center">
                            <span className="text-luxury-black text-base font-bold">
                              {getInitials(nomeUsuario, userEmail)}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="font-['Montserrat'] text-sm font-semibold text-white truncate">
                          {nomeUsuario || nomeExibicao}
                        </p>
                        <p className="font-['Montserrat'] text-xs text-white/40 truncate">
                          {userEmail}
                        </p>
                      </div>
                    </div>

                    <div className="p-2">
                      <button
                        onClick={() => { navigate('/perfil'); setShowMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gold-500/10 rounded-lg transition-all duration-300 cursor-pointer"
                      >
                        <i className="ri-user-settings-line text-gold-400 text-xl"></i>
                        <span className="font-['Montserrat'] text-sm text-white">Meu Perfil</span>
                      </button>

                      <button
                        onClick={() => { navigate('/dashboard/notificacoes'); setShowMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gold-500/10 rounded-lg transition-all duration-300 cursor-pointer"
                      >
                        <i className="ri-notification-3-line text-gold-400 text-xl"></i>
                        <span className="font-['Montserrat'] text-sm text-white">Notificações</span>
                      </button>

                      <button
                        onClick={() => { navigate('/dashboard/templates'); setShowMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gold-500/10 rounded-lg transition-all duration-300 cursor-pointer"
                      >
                        <i className="ri-mail-settings-line text-gold-400 text-xl"></i>
                        <span className="font-['Montserrat'] text-sm text-white">Templates de Email</span>
                      </button>

                      <button
                        onClick={() => { navigate('/dashboard/historico-emails'); setShowMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gold-500/10 rounded-lg transition-all duration-300 cursor-pointer"
                      >
                        <i className="ri-mail-line text-gold-400 text-xl"></i>
                        <span className="font-['Montserrat'] text-sm text-white">Histórico de Emails</span>
                      </button>

                      <button
                        onClick={() => { navigate('/dashboard/backups'); setShowMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gold-500/10 rounded-lg transition-all duration-300 cursor-pointer"
                      >
                        <i className="ri-database-2-line text-gold-400 text-xl"></i>
                        <span className="font-['Montserrat'] text-sm text-white">Gerenciar Backups</span>
                      </button>

                      <a
                        href="/"
                        className="flex items-center gap-3 px-4 py-3 hover:bg-gold-500/10 rounded-lg transition-all duration-300 cursor-pointer"
                      >
                        <i className="ri-home-line text-gold-400 text-xl"></i>
                        <span className="font-['Montserrat'] text-sm text-white">Ir para Home</span>
                      </a>

                      <button
                        onClick={onLogout}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-500/10 rounded-lg transition-all duration-300 cursor-pointer"
                      >
                        <i className="ri-logout-box-line text-red-400 text-xl"></i>
                        <span className="font-['Montserrat'] text-sm text-white">Sair</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Painel de Notificações */}
      <NotificationsPanel
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
      />
    </>
  );
};

export default DashboardHeader;
